import React, { useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";

import { LoadingDiv, SweetAlertShow } from "../../master/Master";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import Master from "../../student/Master";

import {
  studentProfilePictureBulkUpload,
  studentProfilePictureUrlBulkUpload,
} from "../../../api/StudentImport";

const ALLOWED_EXT = ["jpg", "jpeg", "png"];
const MAX_SIZE = 2 * 1024 * 1024;
const CHUNK_SIZE = 20;
const URL_CHUNK_SIZE = 50;

const parseFileName = (fileName) => {
  const dot = fileName.lastIndexOf(".");
  const ext = dot > -1 ? fileName.slice(dot + 1).toLowerCase() : "";
  const base = dot > -1 ? fileName.slice(0, dot) : fileName;
  const idx = base.indexOf("_");

  return {
    ext,
    firstName: idx > -1 ? base.slice(0, idx).trim() : "",
    registration: idx > -1 ? base.slice(idx + 1).trim() : "",
  };
};

const normalizeHeader = (value) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getExcelValue = (row, names) => {
  const normalized = {};
  Object.keys(row || {}).forEach((key) => {
    normalized[normalizeHeader(key)] = row[key];
  });

  for (const name of names) {
    const value = normalized[normalizeHeader(name)];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

const isValidHttpUrl = (value) => {
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

export default function StudentProfilePictureImport() {
  const imageInputRef = useRef(null);
  const excelInputRef = useRef(null);

  const [mode, setMode] = useState("folder"); // folder | excel
  const [items, setItems] = useState([]);
  const [excelRows, setExcelRows] = useState([]);
  const [excelFileName, setExcelFileName] = useState("");

  const [loading, setLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [verifyName, setVerifyName] = useState(true);
  const [dragOver, setDragOver] = useState(false);

  const itemsRef = useRef(items);
  itemsRef.current = items;

  useEffect(
    () => () => {
      itemsRef.current.forEach((item) => {
        if (item.url) URL.revokeObjectURL(item.url);
      });
    },
    [],
  );

  const resetFolder = () => {
    items.forEach((item) => {
      if (item.url) URL.revokeObjectURL(item.url);
    });
    setItems([]);
    setProgress(0);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const resetExcel = () => {
    setExcelRows([]);
    setExcelFileName("");
    setProgress(0);
    if (excelInputRef.current) excelInputRef.current.value = "";
  };

  const switchMode = (nextMode) => {
    if (isUploading) return;
    setMode(nextMode);
    setProgress(0);
  };

  // ============================================================
  // FOLDER / IMAGE MODE
  // ============================================================

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;

    setItems((prev) => {
      const existing = new Set(prev.map((p) => p.file.name.toLowerCase()));
      const added = [];

      incoming.forEach((file) => {
        const key = file.name.toLowerCase();
        if (existing.has(key)) return;
        existing.add(key);

        added.push({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          file,
          url: URL.createObjectURL(file),
          status: "pending",
          message: "",
        });
      });

      return [...prev, ...added];
    });

    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const removeItem = (id) => {
    setItems((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.url) URL.revokeObjectURL(target.url);
      return prev.filter((p) => p.id !== id);
    });
  };

  const folderValidationErrors = useMemo(() => {
    const regCount = {};
    const parsed = items.map((it) => {
      const p = parseFileName(it.file.name);
      const key = p.registration.toLowerCase();
      if (key) regCount[key] = (regCount[key] || 0) + 1;
      return p;
    });

    return items.map((it, index) => {
      const errors = [];
      const p = parsed[index];

      if (!ALLOWED_EXT.includes(p.ext))
        errors.push("Only JPG/JPEG/PNG allowed");
      if (it.file.size > MAX_SIZE) errors.push("Maximum file size is 2 MB");
      if (!p.firstName || !p.registration) {
        errors.push("Use firstname_registrationnumber.ext");
      }
      if (p.registration && regCount[p.registration.toLowerCase()] > 1) {
        errors.push("Duplicate registration number");
      }
      return errors;
    });
  }, [items]);

  const applyFolderResult = (chunk, data) => {
    const successMap = new Map(
      (data?.success || []).map((row) => [
        String(row.file || "").toLowerCase(),
        row,
      ]),
    );
    const failedMap = new Map(
      (data?.failed || []).map((row) => [
        String(row.file || "").toLowerCase(),
        row,
      ]),
    );

    const fallback = Array.isArray(data?.message)
      ? data.message.join(", ")
      : data?.message || "Upload failed";

    setItems((prev) =>
      prev.map((item) => {
        if (!chunk.some((row) => row.id === item.id)) return item;

        const key = item.file.name.toLowerCase();

        if (successMap.has(key)) {
          const result = successMap.get(key);
          return {
            ...item,
            status: "success",
            message: `${result.student || "Student"} (${result.registration_no || ""})`,
          };
        }

        if (failedMap.has(key)) {
          return {
            ...item,
            status: "error",
            message: failedMap.get(key)?.message || fallback,
          };
        }

        return { ...item, status: "error", message: fallback };
      }),
    );
  };

  const runFolderUpload = async (list) => {
    setLoading(true);
    setIsUploading(true);
    setProgress(0);

    try {
      for (let i = 0; i < list.length; i += CHUNK_SIZE) {
        const chunk = list.slice(i, i + CHUNK_SIZE);
        const formData = new FormData();

        chunk.forEach((item) => formData.append("images[]", item.file));
        formData.append("verify_name", verifyName ? "1" : "0");

        try {
          const response = await studentProfilePictureBulkUpload(formData);
          applyFolderResult(chunk, response?.data);
        } catch (error) {
          const data = error?.response?.data;
          applyFolderResult(
            chunk,
            data?.failed ? data : { message: data?.message || error?.message },
          );
        }

        setProgress(
          Math.round(
            (Math.min(i + CHUNK_SIZE, list.length) / list.length) * 100,
          ),
        );
      }
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };

  const handleFolderUpload = async () => {
    if (isUploading) return;

    const list = items.filter(
      (item, index) =>
        !folderValidationErrors[index]?.length && item.status !== "success",
    );

    if (!list.length) {
      return SweetAlertShow(
        "error",
        "No Valid Images",
        "Select valid images and fix validation errors first.",
      );
    }

    await runFolderUpload(list);
    SweetAlertShow("info", "Upload Finished", "Check each image status below.");
  };

  const retryFolderFailed = async () => {
    const list = items.filter(
      (item, index) =>
        !folderValidationErrors[index]?.length && item.status === "error",
    );

    if (!list.length) {
      return SweetAlertShow("info", "No Failed Images", "Nothing to retry.");
    }

    await runFolderUpload(list);
  };

  // ============================================================
  // EXCEL + URL MODE
  // ============================================================

  const handleExcelFile = async (file) => {
    if (!file) return;

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!["xlsx", "xls", "csv"].includes(extension)) {
      if (excelInputRef.current) excelInputRef.current.value = "";
      return SweetAlertShow(
        "error",
        "Invalid File",
        "Please select an .xlsx, .xls or .csv file.",
      );
    }

    setLoading(true);

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheetName = workbook.SheetNames?.[0];

      if (!sheetName) throw new Error("Excel file has no worksheet.");

      const json = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], {
        defval: "",
        raw: false,
      });

      const parsed = json.map((row, index) => {
        const registrationNo = getExcelValue(row, [
          "registrationnumber",
          "registrationno",
          "registration_no",
          "registration number",
          "regno",
          "reg no",
        ]);

        const photoUrl = getExcelValue(row, [
          "photourl",
          "photo_url",
          "photo url",
          "imageurl",
          "image_url",
          "image url",
          "profilepicture",
          "profile_picture",
        ]);

        return {
          id: `excel-${Date.now()}-${index}`,
          excelRow: index + 2,
          registration_no: registrationNo,
          photo_url: photoUrl,
          status: "pending",
          message: "",
          student: "",
          path: "",
        };
      });

      if (!parsed.length) {
        throw new Error("No data rows found in the first worksheet.");
      }

      setExcelRows(parsed);
      setExcelFileName(file.name);
      setProgress(0);
    } catch (error) {
      setExcelRows([]);
      setExcelFileName("");
      SweetAlertShow(
        "error",
        "Unable to Read Excel",
        error?.message || "Please check the Excel file.",
      );
    } finally {
      setLoading(false);
      if (excelInputRef.current) excelInputRef.current.value = "";
    }
  };

  const excelValidationErrors = useMemo(() => {
    const regCount = {};

    excelRows.forEach((row) => {
      const key = String(row.registration_no || "")
        .trim()
        .toLowerCase();
      if (key) regCount[key] = (regCount[key] || 0) + 1;
    });

    return excelRows.map((row) => {
      const errors = [];
      const registrationNo = String(row.registration_no || "").trim();
      const photoUrl = String(row.photo_url || "").trim();

      if (!registrationNo) errors.push("Registration number is required");
      if (!photoUrl) errors.push("Photo URL is required");
      else if (!isValidHttpUrl(photoUrl)) errors.push("Invalid HTTP/HTTPS URL");

      if (registrationNo && regCount[registrationNo.toLowerCase()] > 1) {
        errors.push("Duplicate registration number in Excel");
      }

      return errors;
    });
  }, [excelRows]);

  const applyUrlResults = (chunk, data) => {
    const successMap = new Map(
      (data?.success || []).map((row) => [
        String(row.registration_no || "").toLowerCase(),
        row,
      ]),
    );

    const failedMap = new Map(
      (data?.failed || []).map((row) => [
        String(row.registration_no || "").toLowerCase(),
        row,
      ]),
    );

    const fallback = Array.isArray(data?.message)
      ? data.message.join(", ")
      : data?.message || "Import failed";

    setExcelRows((prev) =>
      prev.map((row) => {
        if (!chunk.some((item) => item.id === row.id)) return row;

        const key = String(row.registration_no || "").toLowerCase();

        if (successMap.has(key)) {
          const result = successMap.get(key);
          return {
            ...row,
            status: "success",
            message: "Downloaded and saved",
            student: result.student || "",
            path: result.path || "",
          };
        }

        if (failedMap.has(key)) {
          return {
            ...row,
            status: "error",
            message: failedMap.get(key)?.message || fallback,
          };
        }

        return { ...row, status: "error", message: fallback };
      }),
    );
  };

  const runUrlUpload = async (list) => {
    setLoading(true);
    setIsUploading(true);
    setProgress(0);

    try {
      for (let i = 0; i < list.length; i += URL_CHUNK_SIZE) {
        const chunk = list.slice(i, i + URL_CHUNK_SIZE);

        const payload = {
          rows: chunk.map((row) => ({
            registration_no: row.registration_no,
            photo_url: row.photo_url,
          })),
        };

        try {
          const response = await studentProfilePictureUrlBulkUpload(payload);
          applyUrlResults(chunk, response?.data);
        } catch (error) {
          const data = error?.response?.data;
          applyUrlResults(
            chunk,
            data?.failed ? data : { message: data?.message || error?.message },
          );
        }

        setProgress(
          Math.round(
            (Math.min(i + URL_CHUNK_SIZE, list.length) / list.length) * 100,
          ),
        );
      }
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };

  const handleUrlUpload = async () => {
    const list = excelRows.filter(
      (row, index) =>
        !excelValidationErrors[index]?.length && row.status !== "success",
    );

    if (!list.length) {
      return SweetAlertShow(
        "error",
        "No Valid Rows",
        "Upload an Excel file with registrationnumber and photourl columns.",
      );
    }

    await runUrlUpload(list);
    SweetAlertShow(
      "info",
      "URL Import Finished",
      "Images were downloaded by the backend and saved to student profiles.",
    );
  };

  const retryUrlFailed = async () => {
    const list = excelRows.filter(
      (row, index) =>
        !excelValidationErrors[index]?.length && row.status === "error",
    );

    if (!list.length) {
      return SweetAlertShow("info", "No Failed Rows", "Nothing to retry.");
    }

    await runUrlUpload(list);
  };

  const downloadTemplate = () => {
    const rows = [
      {
        registrationnumber: "1023",
        photourl: "https://example.com/student-photo.jpg",
      },
      {
        registrationnumber: "1024",
        photourl: "https://example.com/student-photo-2.png",
      },
    ];

    const worksheet = XLSX.utils.json_to_sheet(rows);
    worksheet["!cols"] = [{ wch: 24 }, { wch: 60 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Profile Pictures");
    XLSX.writeFile(workbook, "student_profile_picture_url_template.xlsx");
  };

  const downloadFailed = () => {
    const rows =
      mode === "folder"
        ? items
            .map((item, index) => ({
              source: "Folder Upload",
              file_name: item.file.name,
              registration_no: parseFileName(item.file.name).registration,
              validation_error: folderValidationErrors[index]?.join(", ") || "",
              upload_error: item.status === "error" ? item.message : "",
            }))
            .filter((row) => row.validation_error || row.upload_error)
        : excelRows
            .map((row, index) => ({
              source: "Excel URL",
              excel_row: row.excelRow,
              registration_no: row.registration_no,
              photo_url: row.photo_url,
              validation_error: excelValidationErrors[index]?.join(", ") || "",
              upload_error: row.status === "error" ? row.message : "",
            }))
            .filter((row) => row.validation_error || row.upload_error);

    if (!rows.length) {
      return SweetAlertShow(
        "info",
        "No Failed Records",
        "Nothing to download.",
      );
    }

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Failed");
    XLSX.writeFile(workbook, "failed_student_profile_pictures.xlsx");
  };

  // ============================================================
  // COUNTS
  // ============================================================

  const folderValidationCount = folderValidationErrors.filter(
    (errors) => errors.length,
  ).length;

  const excelValidationCount = excelValidationErrors.filter(
    (errors) => errors.length,
  ).length;

  const selectedCount = mode === "folder" ? items.length : excelRows.length;
  const successCount =
    mode === "folder"
      ? items.filter((item) => item.status === "success").length
      : excelRows.filter((row) => row.status === "success").length;
  const failedCount =
    mode === "folder"
      ? items.filter((item) => item.status === "error").length
      : excelRows.filter((row) => row.status === "error").length;
  const validationCount =
    mode === "folder" ? folderValidationCount : excelValidationCount;

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar profile-import-page">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="content-wrapper">
              <div className="container-fluid flex-grow-1 container-p-y">
                <div className="row">
                  <Master />

                  <div className="col-12">
                    <div className="profile-import-hero">
                      <div>
                        <div className="profile-import-eyebrow">
                          <i className="ri-image-2-line" />
                          STUDENT PROFILE PICTURES
                        </div>
                        <h4>Bulk Profile Picture Import</h4>
                        <p>
                          Upload named image files directly, or import an Excel
                          sheet containing registration numbers and remote photo
                          URLs.
                        </p>
                      </div>

                      <div className="profile-import-hero-icon">
                        <i className="ri-user-smile-line" />
                      </div>
                    </div>

                    <div className="profile-import-mode-card">
                      <div className="mode-title">Choose import method</div>

                      <div className="import-mode-grid">
                        <button
                          type="button"
                          className={`import-mode-option ${
                            mode === "folder" ? "active" : ""
                          }`}
                          onClick={() => switchMode("folder")}
                          disabled={isUploading}
                        >
                          <span className="mode-icon">
                            <i className="ri-folder-upload-line" />
                          </span>
                          <span>
                            <strong>Upload from Folder</strong>
                            <small>
                              Select multiple JPG/PNG files named using
                              registration no.
                            </small>
                          </span>
                          <i className="ri-checkbox-circle-fill mode-check" />
                        </button>

                        <button
                          type="button"
                          className={`import-mode-option ${
                            mode === "excel" ? "active" : ""
                          }`}
                          onClick={() => switchMode("excel")}
                          disabled={isUploading}
                        >
                          <span className="mode-icon">
                            <i className="ri-file-excel-2-line" />
                          </span>
                          <span>
                            <strong>Excel with Photo URLs</strong>
                            <small>
                              registrationnumber + photourl; backend downloads
                              images.
                            </small>
                          </span>
                          <i className="ri-checkbox-circle-fill mode-check" />
                        </button>
                      </div>
                    </div>

                    {mode === "folder" ? (
                      <div className="profile-import-card">
                        <div className="section-heading">
                          <div>
                            <h5>Upload Images from Folder</h5>
                            <p>
                              Format:{" "}
                              <code>
                                firstname_middlename_lastname_registrationnumber.jpg
                                or firstname_lastname_registartionnumber.jpg or
                                firstname_registrationnumber.jpg
                              </code>
                              . Example: <code>Rahul_1023.jpg</code>. Max 2 MB
                              each.
                            </p>
                          </div>
                        </div>

                        <div
                          className={`modern-dropzone ${dragOver ? "dragging" : ""}`}
                          onClick={() => imageInputRef.current?.click()}
                          onDragOver={(event) => {
                            event.preventDefault();
                            setDragOver(true);
                          }}
                          onDragLeave={() => setDragOver(false)}
                          onDrop={(event) => {
                            event.preventDefault();
                            setDragOver(false);
                            addFiles(event.dataTransfer.files);
                          }}
                        >
                          <div className="dropzone-icon">
                            <i className="ri-folder-image-line" />
                          </div>
                          <strong>Drop student images here</strong>
                          <span>or click to select multiple images</span>
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                          >
                            <i className="ri-upload-cloud-2-line me-1" />
                            Select Images
                          </button>

                          <input
                            ref={imageInputRef}
                            type="file"
                            multiple
                            accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                            className="d-none"
                            onChange={(event) => addFiles(event.target.files)}
                          />
                        </div>

                        <label className="verify-option">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={verifyName}
                            onChange={(event) =>
                              setVerifyName(event.target.checked)
                            }
                          />
                          <span>
                            <strong>Verify first name</strong>
                            <small>
                              Also verify that the first name in the filename
                              matches the student's first name.
                            </small>
                          </span>
                        </label>
                      </div>
                    ) : (
                      <div className="profile-import-card">
                        <div className="section-heading excel-heading">
                          <div>
                            <h5>Upload Excel with Image URLs</h5>
                            <p>
                              Required columns: <code>registrationnumber</code>{" "}
                              and <code>photourl</code>. The backend downloads
                              and stores each image on your server.
                            </p>
                          </div>

                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={downloadTemplate}
                          >
                            <i className="ri-download-2-line me-1" />
                            Download Template
                          </button>
                        </div>

                        <div
                          className="modern-dropzone excel-dropzone"
                          onClick={() => excelInputRef.current?.click()}
                        >
                          <div className="dropzone-icon excel">
                            <i className="ri-file-excel-2-line" />
                          </div>
                          <strong>
                            {excelFileName || "Select Excel / CSV file"}
                          </strong>
                          <span>
                            Supported: .xlsx, .xls, .csv • first worksheet is
                            used
                          </span>
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                          >
                            <i className="ri-file-upload-line me-1" />
                            Choose Excel
                          </button>

                          <input
                            ref={excelInputRef}
                            type="file"
                            accept=".xlsx,.xls,.csv"
                            className="d-none"
                            onChange={(event) =>
                              handleExcelFile(event.target.files?.[0])
                            }
                          />
                        </div>

                        <div className="excel-format-note">
                          <i className="ri-information-line" />
                          <div>
                            <strong>Excel example</strong>
                            <span>
                              registrationnumber: <b>1023</b> &nbsp;|&nbsp;
                              photourl:{" "}
                              <b>https://domain.com/photos/1023.jpg</b>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="profile-stat-grid">
                      <div className="profile-stat-card">
                        <span className="stat-icon primary">
                          <i className="ri-stack-line" />
                        </span>
                        <div>
                          <small>
                            {mode === "folder"
                              ? "Selected Images"
                              : "Excel Rows"}
                          </small>
                          <strong>{selectedCount}</strong>
                        </div>
                      </div>

                      <div className="profile-stat-card">
                        <span className="stat-icon success">
                          <i className="ri-checkbox-circle-line" />
                        </span>
                        <div>
                          <small>Successful</small>
                          <strong>{successCount}</strong>
                        </div>
                      </div>

                      <div className="profile-stat-card">
                        <span className="stat-icon danger">
                          <i className="ri-close-circle-line" />
                        </span>
                        <div>
                          <small>Failed</small>
                          <strong>{failedCount}</strong>
                        </div>
                      </div>

                      <div className="profile-stat-card">
                        <span className="stat-icon warning">
                          <i className="ri-error-warning-line" />
                        </span>
                        <div>
                          <small>Validation Errors</small>
                          <strong>{validationCount}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="profile-import-card result-card">
                      <div className="result-toolbar">
                        <div>
                          <h5>
                            {mode === "folder"
                              ? "Selected Images"
                              : "Excel Import Preview"}
                          </h5>
                          <p>
                            Review validation and upload results before leaving
                            this page.
                          </p>
                        </div>

                        <div className="result-actions">
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={
                              mode === "folder"
                                ? handleFolderUpload
                                : handleUrlUpload
                            }
                            disabled={isUploading || !selectedCount}
                          >
                            <i className="ri-upload-cloud-line me-1" />
                            {isUploading ? "Processing..." : "Start Import"}
                          </button>

                          <button
                            className="btn btn-outline-warning btn-sm"
                            onClick={
                              mode === "folder"
                                ? retryFolderFailed
                                : retryUrlFailed
                            }
                            disabled={isUploading || !failedCount}
                          >
                            <i className="ri-refresh-line me-1" />
                            Retry Failed
                          </button>

                          <button
                            className="btn btn-outline-danger btn-sm"
                            onClick={downloadFailed}
                            disabled={!failedCount && !validationCount}
                          >
                            <i className="ri-file-download-line me-1" />
                            Failed Report
                          </button>

                          <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={
                              mode === "folder" ? resetFolder : resetExcel
                            }
                            disabled={isUploading || !selectedCount}
                          >
                            <i className="ri-delete-bin-6-line me-1" />
                            Clear
                          </button>
                        </div>
                      </div>

                      {(isUploading || progress > 0) && (
                        <div className="import-progress">
                          <div className="progress-label">
                            <span>
                              {isUploading
                                ? "Importing profile pictures..."
                                : "Completed"}
                            </span>
                            <strong>{progress}%</strong>
                          </div>
                          <div className="progress">
                            <div
                              className="progress-bar"
                              role="progressbar"
                              style={{ width: `${progress}%` }}
                            />
                          </div>
                        </div>
                      )}

                      <div className="table-responsive profile-table-wrap">
                        {mode === "folder" ? (
                          <table className="table profile-import-table align-middle mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Preview</th>
                                <th>File</th>
                                <th>First Name</th>
                                <th>Registration No.</th>
                                <th>Size</th>
                                <th>Status</th>
                                <th>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {items.map((item, index) => {
                                const parsed = parseFileName(item.file.name);
                                const errors =
                                  folderValidationErrors[index] || [];

                                return (
                                  <tr key={item.id}>
                                    <td>{index + 1}</td>
                                    <td>
                                      <img
                                        className="student-photo-preview"
                                        src={item.url}
                                        alt=""
                                        loading="lazy"
                                      />
                                    </td>
                                    <td>
                                      <strong className="file-name">
                                        {item.file.name}
                                      </strong>
                                    </td>
                                    <td>{parsed.firstName || "-"}</td>
                                    <td>
                                      <span className="registration-chip">
                                        {parsed.registration || "-"}
                                      </span>
                                    </td>
                                    <td>
                                      {(item.file.size / 1024).toFixed(0)} KB
                                    </td>
                                    <td>
                                      <StatusCell
                                        status={item.status}
                                        message={item.message}
                                        errors={errors}
                                      />
                                    </td>
                                    <td>
                                      {item.status === "error" &&
                                        !errors.length && (
                                          <button
                                            className="btn btn-icon btn-sm btn-outline-warning me-1"
                                            disabled={isUploading}
                                            onClick={() =>
                                              runFolderUpload([item])
                                            }
                                            title="Retry"
                                          >
                                            <i className="ri-refresh-line" />
                                          </button>
                                        )}

                                      <button
                                        className="btn btn-icon btn-sm btn-outline-danger"
                                        disabled={isUploading}
                                        onClick={() => removeItem(item.id)}
                                        title="Remove"
                                      >
                                        <i className="ri-delete-bin-line" />
                                      </button>
                                    </td>
                                  </tr>
                                );
                              })}

                              {!items.length && (
                                <EmptyTable
                                  colSpan={8}
                                  icon="ri-image-line"
                                  title="No images selected"
                                  subtitle="Choose Upload from Folder and select student images."
                                />
                              )}
                            </tbody>
                          </table>
                        ) : (
                          <table className="table profile-import-table align-middle mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Excel Row</th>
                                <th>Registration No.</th>
                                <th>Photo Preview</th>
                                <th>Photo URL</th>
                                <th>Student</th>
                                <th>Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {excelRows.map((row, index) => {
                                const errors =
                                  excelValidationErrors[index] || [];

                                return (
                                  <tr key={row.id}>
                                    <td>{index + 1}</td>
                                    <td>{row.excelRow}</td>
                                    <td>
                                      <span className="registration-chip">
                                        {row.registration_no || "-"}
                                      </span>
                                    </td>
                                    <td>
                                      {isValidHttpUrl(row.photo_url) ? (
                                        <img
                                          className="student-photo-preview"
                                          src={row.photo_url}
                                          alt=""
                                          loading="lazy"
                                          referrerPolicy="no-referrer"
                                          onError={(event) => {
                                            event.currentTarget.style.visibility =
                                              "hidden";
                                          }}
                                        />
                                      ) : (
                                        "-"
                                      )}
                                    </td>
                                    <td>
                                      <div
                                        className="url-cell"
                                        title={row.photo_url}
                                      >
                                        {row.photo_url || "-"}
                                      </div>
                                    </td>
                                    <td>{row.student || "-"}</td>
                                    <td>
                                      <StatusCell
                                        status={row.status}
                                        message={row.message}
                                        errors={errors}
                                      />
                                    </td>
                                  </tr>
                                );
                              })}

                              {!excelRows.length && (
                                <EmptyTable
                                  colSpan={7}
                                  icon="ri-file-excel-2-line"
                                  title="No Excel data loaded"
                                  subtitle="Upload the template or your own Excel file to preview records."
                                />
                              )}
                            </tbody>
                          </table>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .profile-import-page {
          --pi-border: var(--bs-border-color, #e6e8ef);
          --pi-surface: var(--bs-body-bg, #fff);
          --pi-soft: rgba(105, 92, 254, .07);
          --pi-text: var(--bs-body-color, #344054);
          --pi-muted: #7d8799;
          --pi-shadow: 0 8px 28px rgba(16, 24, 40, .06);
        }

        .profile-import-hero {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 18px;
          margin-bottom: 16px;
          padding: 20px 22px;
          border: 1px solid var(--pi-border);
          border-radius: 18px;
          background:
            radial-gradient(circle at 90% 10%, rgba(105,92,254,.13), transparent 34%),
            var(--pi-surface);
          box-shadow: var(--pi-shadow);
        }

        .profile-import-eyebrow {
          display: flex;
          align-items: center;
          gap: 7px;
          margin-bottom: 6px;
          color: #6d5dfc;
          font-size: 10px;
          font-weight: 800;
          letter-spacing: .09em;
        }

        .profile-import-hero h4,
        .profile-import-card h5 {
          margin: 0;
          color: var(--pi-text);
          font-weight: 800;
        }

        .profile-import-hero p,
        .section-heading p,
        .result-toolbar p {
          margin: 5px 0 0;
          color: var(--pi-muted);
          font-size: 12px;
        }

        .profile-import-hero-icon {
          width: 54px;
          height: 54px;
          flex: 0 0 54px;
          display: grid;
          place-items: center;
          border-radius: 16px;
          background: linear-gradient(135deg, #6d5dfc, #8878ff);
          color: #fff;
          box-shadow: 0 10px 24px rgba(109,93,252,.25);
        }

        .profile-import-hero-icon i { font-size: 25px; }

        .profile-import-mode-card,
        .profile-import-card {
          margin-bottom: 16px;
          padding: 18px;
          border: 1px solid var(--pi-border);
          border-radius: 16px;
          background: var(--pi-surface);
          box-shadow: var(--pi-shadow);
        }

        .mode-title {
          margin-bottom: 10px;
          color: var(--pi-text);
          font-size: 12px;
          font-weight: 800;
        }

        .import-mode-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }

        .import-mode-option {
          position: relative;
          width: 100%;
          min-height: 78px;
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 13px;
          text-align: left;
          border: 1px solid var(--pi-border);
          border-radius: 14px;
          background: var(--pi-surface);
          color: var(--pi-text);
          transition: .18s ease;
        }

        .import-mode-option:hover {
          border-color: rgba(109,93,252,.45);
          transform: translateY(-1px);
        }

        .import-mode-option.active {
          border-color: #6d5dfc;
          background: var(--pi-soft);
          box-shadow: 0 0 0 3px rgba(109,93,252,.08);
        }

        .mode-icon {
          width: 42px;
          height: 42px;
          flex: 0 0 42px;
          display: grid;
          place-items: center;
          border-radius: 12px;
          background: rgba(109,93,252,.10);
          color: #6d5dfc;
        }

        .mode-icon i { font-size: 20px; }

        .import-mode-option strong,
        .import-mode-option small {
          display: block;
        }

        .import-mode-option strong { font-size: 13px; }
        .import-mode-option small {
          margin-top: 3px;
          color: var(--pi-muted);
          font-size: 10px;
        }

        .mode-check {
          position: absolute;
          top: 10px;
          right: 10px;
          color: #6d5dfc;
          opacity: 0;
        }

        .import-mode-option.active .mode-check { opacity: 1; }

        .section-heading,
        .result-toolbar {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 14px;
        }

        .modern-dropzone {
          min-height: 190px;
          padding: 28px 20px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          border: 1.5px dashed rgba(109,93,252,.42);
          border-radius: 16px;
          background: rgba(109,93,252,.035);
          cursor: pointer;
          transition: .18s ease;
        }

        .modern-dropzone:hover,
        .modern-dropzone.dragging {
          border-color: #6d5dfc;
          background: rgba(109,93,252,.08);
        }

        .dropzone-icon {
          width: 54px;
          height: 54px;
          display: grid;
          place-items: center;
          margin-bottom: 10px;
          border-radius: 16px;
          background: rgba(109,93,252,.11);
          color: #6d5dfc;
        }

        .dropzone-icon.excel {
          background: rgba(40,167,69,.11);
          color: #28a745;
        }

        .dropzone-icon i { font-size: 25px; }
        .modern-dropzone strong {
          color: var(--pi-text);
          font-size: 14px;
        }
        .modern-dropzone > span {
          margin: 4px 0 12px;
          color: var(--pi-muted);
          font-size: 11px;
        }

        .verify-option,
        .excel-format-note {
          margin-top: 13px;
          padding: 11px 12px;
          display: flex;
          align-items: flex-start;
          gap: 10px;
          border: 1px solid var(--pi-border);
          border-radius: 12px;
          background: rgba(127,135,153,.045);
        }

        .verify-option strong,
        .verify-option small,
        .excel-format-note strong,
        .excel-format-note span {
          display: block;
        }

        .verify-option strong,
        .excel-format-note strong {
          color: var(--pi-text);
          font-size: 11px;
        }

        .verify-option small,
        .excel-format-note span {
          margin-top: 2px;
          color: var(--pi-muted);
          font-size: 10px;
        }

        .excel-format-note > i {
          color: #6d5dfc;
          font-size: 18px;
        }

        .profile-stat-grid {
          display: grid;
          grid-template-columns: repeat(4, minmax(0,1fr));
          gap: 12px;
          margin-bottom: 16px;
        }

        .profile-stat-card {
          min-height: 82px;
          padding: 13px;
          display: flex;
          align-items: center;
          gap: 11px;
          border: 1px solid var(--pi-border);
          border-radius: 14px;
          background: var(--pi-surface);
          box-shadow: var(--pi-shadow);
        }

        .stat-icon {
          width: 38px;
          height: 38px;
          flex: 0 0 38px;
          display: grid;
          place-items: center;
          border-radius: 11px;
        }

        .stat-icon.primary { color:#6d5dfc; background:rgba(109,93,252,.11); }
        .stat-icon.success { color:#28a745; background:rgba(40,167,69,.11); }
        .stat-icon.danger { color:#dc3545; background:rgba(220,53,69,.11); }
        .stat-icon.warning { color:#f0a000; background:rgba(255,193,7,.13); }

        .profile-stat-card small {
          display: block;
          color: var(--pi-muted);
          font-size: 10px;
        }

        .profile-stat-card strong {
          display: block;
          margin-top: 2px;
          color: var(--pi-text);
          font-size: 19px;
        }

        .result-actions {
          display: flex;
          flex-wrap: wrap;
          gap: 7px;
        }

        .import-progress {
          margin-bottom: 14px;
          padding: 11px;
          border-radius: 11px;
          background: rgba(109,93,252,.06);
        }

        .progress-label {
          display: flex;
          justify-content: space-between;
          margin-bottom: 6px;
          color: var(--pi-text);
          font-size: 10px;
        }

        .profile-table-wrap {
          border: 1px solid var(--pi-border);
          border-radius: 12px;
        }

        .profile-import-table {
          color: var(--pi-text);
          font-size: 11px;
        }

        .profile-import-table thead th {
          padding: 10px 9px;
          color: var(--pi-muted);
          background: rgba(127,135,153,.07);
          border-bottom-color: var(--pi-border);
          font-size: 9px;
          font-weight: 800;
          letter-spacing: .04em;
          text-transform: uppercase;
          white-space: nowrap;
        }

        .profile-import-table td {
          padding: 9px;
          border-color: var(--pi-border);
          vertical-align: middle;
        }

        .student-photo-preview {
          width: 42px;
          height: 42px;
          object-fit: cover;
          border: 1px solid var(--pi-border);
          border-radius: 10px;
          background: rgba(127,135,153,.08);
        }

        .file-name {
          display: block;
          max-width: 220px;
          overflow: hidden;
          color: var(--pi-text);
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .registration-chip {
          display: inline-flex;
          padding: 4px 7px;
          border-radius: 7px;
          background: rgba(109,93,252,.09);
          color: #6d5dfc;
          font-weight: 700;
        }

        .url-cell {
          max-width: 330px;
          overflow: hidden;
          color: var(--pi-muted);
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .status-message {
          max-width: 280px;
          margin-top: 3px;
          font-size: 9px;
          line-height: 1.35;
        }

        .empty-import-state {
          padding: 34px 15px !important;
          text-align: center;
          color: var(--pi-muted);
        }

        .empty-import-state i {
          display: block;
          margin-bottom: 5px;
          font-size: 25px;
          color: #6d5dfc;
        }

        .empty-import-state strong {
          display: block;
          color: var(--pi-text);
        }

        .empty-import-state small { font-size: 10px; }

        html.dark-style .profile-import-page {
          --pi-border: #3b3a54;
          --pi-surface: #29283f;
          --pi-soft: rgba(139,124,255,.12);
          --pi-text: #ececf5;
          --pi-muted: #a4a6ba;
          --pi-shadow: 0 8px 28px rgba(0,0,0,.16);
        }

        html.dark-style .profile-import-table,
        html.dark-style .profile-import-table td,
        html.dark-style .profile-import-table th {
          color: var(--pi-text);
          background-color: transparent;
        }

        html.dark-style .profile-import-table thead th {
          background: rgba(255,255,255,.04);
          color: var(--pi-muted);
        }

        html.dark-style .profile-import-card code {
          color: #b9afff;
          background: rgba(139,124,255,.10);
        }

        @media (max-width: 991.98px) {
          .profile-stat-grid { grid-template-columns: repeat(2, minmax(0,1fr)); }
          .import-mode-grid { grid-template-columns: 1fr; }
          .section-heading,
          .result-toolbar { align-items: flex-start; flex-direction: column; }
        }

        @media (max-width: 575.98px) {
          .profile-import-hero { padding: 16px; }
          .profile-import-hero-icon { display: none; }
          .profile-import-mode-card,
          .profile-import-card { padding: 13px; border-radius: 13px; }
          .profile-stat-grid { grid-template-columns: 1fr 1fr; gap: 8px; }
          .profile-stat-card { min-height: 72px; padding: 10px; }
          .modern-dropzone { min-height: 165px; padding: 20px 12px; }
          .result-actions { width: 100%; }
          .result-actions .btn { flex: 1 1 calc(50% - 7px); }
        }
      `}</style>
    </>
  );
}

function StatusCell({ status, message, errors = [] }) {
  if (errors.length) {
    return (
      <>
        <span className="badge bg-label-warning text-warning">Invalid</span>
        <div className="status-message text-warning">{errors.join(", ")}</div>
      </>
    );
  }

  if (status === "success") {
    return (
      <>
        <span className="badge bg-label-success text-success">
          <i className="ri-check-line me-1" />
          Saved
        </span>
        {message && (
          <div className="status-message text-success">{message}</div>
        )}
      </>
    );
  }

  if (status === "error") {
    return (
      <>
        <span className="badge bg-label-danger text-danger">
          <i className="ri-close-line me-1" />
          Failed
        </span>
        {message && <div className="status-message text-danger">{message}</div>}
      </>
    );
  }

  return <span className="badge bg-label-secondary">Pending</span>;
}

function EmptyTable({ colSpan, icon, title, subtitle }) {
  return (
    <tr>
      <td colSpan={colSpan} className="empty-import-state">
        <i className={icon} />
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </td>
    </tr>
  );
}
